import type RBush from 'rbush';

import { type RBushItem } from '@/microfrontends/map/shared/types/Map';
import { getExtendedTileBBox } from '@/microfrontends/map/shared/utils/map/tiles';

import { type Cluster } from '@/packages/query-state/shared/types/clusters/ClustersResponse';

import { type TPromoPinConfig } from '../../../types/PromoPinConfig';

import { createTileConstructor } from './internal/utils/createTileConstructor';

export type PinsLayerOptions = {
  map: YMaps.Map;
  rbush: RBush<RBushItem>;
  promoPinConfig: TPromoPinConfig;
};

export function definePinsLayer(ymaps: YMaps.IYMaps): void {
  if (ymaps.modules.isDefined('PinsLayer')) {
    return;
  }

  ymaps.modules.define<[YMaps.util.IHd, typeof YMaps.Layer]>(
    'PinsLayer',
    // Два модуля зависимостей в конце - обязательные, хоть и не используются в классе, не удалять
    ['util.hd', 'Layer', 'util.pixelBounds', 'layer.tileContainer.DomContainer'],
    (provide, utilHd, Layer) => {
      class PinsLayer {
        public readonly layer: YMaps.Layer;

        private map: YMaps.Map;
        private mapProjection: YMaps.projection.wgs84Mercator;
        private rbush: RBush<RBushItem>;
        private promoPinConfig: TPromoPinConfig;

        public constructor(options: PinsLayerOptions) {
          this.map = options.map;
          this.mapProjection = this.map.options.get('projection');
          this.rbush = options.rbush;
          this.promoPinConfig = options.promoPinConfig;

          this.layer = new Layer(() => null, {
            tileClass: createTileConstructor({
              dpr: utilHd.getPixelRatio(),
              getClustersForTile: this.getClustersForTile,
              mapProjection: this.mapProjection,
              getPromoPinConfig: () => this.promoPinConfig,
            }),
            tileContainerClass: 'default#dom',
            tileTransparent: true,
          });
        }

        private getClustersForTile = (tileNumber: YMaps.TTileNumber, zoom: number): Cluster[] => {
          const extendedTileBox = getExtendedTileBBox({
            tileNumber,
            zoom,
          });
          const rBushItems = this.rbush.search(extendedTileBox);

          return rBushItems.map(({ cluster }) => cluster);
        };
      }

      provide(PinsLayer);
    },
  );
}
